import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import styles from './styles.module.scss';
import CustomSelect from '@ui/CustomSelect';
import { toast } from 'react-toastify';
import SecureFilePicker from '../../components/SecureFilePicker';
import { useForm, Controller } from 'react-hook-form';
import classNames from 'classnames';
import ApiService from './../../network/ApiService';
import Cities from '../../assets/cities.json';

const AddPlayerForm = ({ teamID }) => {
    const [playerImage, setPlayerImage] = useState(null);
    const [identificationImage, setIdentificationImage] = useState(null);
    const navigate = useNavigate();
    const apiService = new ApiService();

    const { register, handleSubmit, formState: { errors }, reset, control } = useForm({
        defaultValues: {
            name: '',
            number: '',
            birthday: '',
            nationality: '',
            position: '',
            eligibility: 'Warten',
            email: '',
            bank: '',
        }
    });

    const getCountriesOptions = () => {
        return Cities.map(country => ({ value: country.country, label: country.country }));
    };

    const onSubmit = async (data) => {
        try {
            if (!playerImage || !identificationImage) {
                toast.error('Spielerfoto und Ausweis sind erforderlich.');
                return;
            }

            const formData = new FormData();
            formData.append('teamID', teamID);
            formData.append('name', data.name);
            formData.append('number', data.number);
            formData.append('birthday', data.birthday);
            formData.append('nationality', data.nationality?.label || '');
            formData.append('position', data.position?.label || '');
            formData.append('email', data.email);
            formData.append('playerImage', playerImage, playerImage.name);
            formData.append('identificationImage', identificationImage, identificationImage.name);

            await apiService.post('app/register/team', formData);
            navigate('/kader');
        } catch (error) {
            toast.error(error.message || 'Spieler konnte nicht angemeldet werden.');
        }
    };

    return (
        <form className="d-flex flex-column g-20" onSubmit={handleSubmit(onSubmit)}>
            {/* Form Fields */}
            <div className={styles.row}>
                <div className={styles.column}>
                    <label htmlFor="name" style={{ marginBottom: '10px' }}>Name</label>
                    <input
                        id="name"
                        className={classNames('field', { 'field--error': errors.name })}
                        type="text"
                        placeholder="Name"
                        {...register('name', { required: true })}
                    />
                </div>
                <div className={styles.column}>
                    <label htmlFor="number" style={{ marginBottom: '10px' }}>Spieler Nummer</label>
                    <input
                        id="number"
                        className={classNames('field', { 'field--error': errors.number })}
                        type="number"
                        placeholder="Spieler Nummer"
                        {...register('number', { required: true })}
                    />
                </div>
            </div>
            <div className={styles.row}>
                <div className={styles.column}>
                    <label htmlFor="position" style={{ marginBottom: '10px' }}>Position</label>
                    <Controller
                        name="position"
                        control={control}
                        render={({ field }) => (
                            <CustomSelect
                                id="position"
                                options={[
                                    { value: 'Feldspieler', label: 'Feldspieler' },
                                    { value: 'Tormann', label: 'Tormann' },
                                ]}
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="Position"
                                isSearchable={false}
                                variant="basic"
                                innerRef={field.ref}
                            />
                        )}
                    />
                </div>
                <div className={styles.column}>
                    <label htmlFor="email" style={{ marginBottom: '10px' }}>Email</label>
                    <input
                        id="email"
                        className={classNames('field', { 'field--error': errors.email })}
                        type="email"
                        placeholder="Email"
                        {...register('email', { required: true })}
                    />
                </div>
                <div className={styles.column}>
                    <label htmlFor="bank" style={{ marginBottom: '10px' }}>Ersatzbank Spieler</label>
                    <Controller
                        name="bank"
                        control={control}
                        render={({ field }) => (
                            <CustomSelect
                                id="bank"
                                options={[
                                    { value: 'JA', label: 'JA' },
                                    { value: 'NEIN', label: 'NEIN' },
                                ]}
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="Ersatzbank Spieler"
                                isSearchable={false}
                                variant="basic"
                                innerRef={field.ref}
                            />
                        )}
                    />
                </div>
            </div>
            <div className={styles.row}>
                <div className={styles.column}>
                    <label htmlFor="nationality" style={{ marginBottom: '10px' }}>Nationalität</label>
                    <Controller
                        name="nationality"
                        control={control}
                        render={({ field }) => (
                            <CustomSelect
                                id="nationality"
                                options={getCountriesOptions()}
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="Nationalität"
                                isSearchable={true}
                                variant="basic"
                                innerRef={field.ref}
                            />
                        )}
                    />
                </div>
                <div className={styles.column}>
                    <label htmlFor="birthday" style={{ marginBottom: '10px' }}>Geburtstag (Jahr)</label>
                    <input
                        id="birthday"
                        className={classNames('field', { 'field--error': errors.birthday })}
                        type="number"
                        placeholder="Geburtstag (Jahr)"
                        {...register('birthday', { required: true })}
                    />
                </div>
            </div>
            <div className={styles.row}>
                <SecureFilePicker
                    onSelect={setPlayerImage}
                    buttonText={"Spieler Foto Hochladen"}
                    accept="image/png,image/jpeg"
                />
                <SecureFilePicker
                    onSelect={setIdentificationImage}
                    buttonText={"Spieler Ausweis Hochladen"}
                />
            </div>
            <div className={styles.footer}>
                <button className="btn" type="submit">Spieler Anmelden</button>
                <button className="btn btn--outlined" type="reset" onClick={() => reset()}>Reset</button>
            </div>
        </form>
    );
};

AddPlayerForm.propTypes = {
    teamID: PropTypes.string.isRequired,
};

export default AddPlayerForm;
